import { isEmpty, isNotEmpty } from '@intelligence/v3/utils/common';
import { buildSensorTowerTagsParams } from '@intelligence/v3/utils/topcharts';
import { generateMetricKey, generateMetricRatioKey, generateSortMetricKey } from '@intelligence/v3/utils/metric';
import { formatAppMagicGenres } from '@intelligence/v3/utils/genre';
import { SortType } from '@home/enums/common';
import { Relation } from '@intelligence/components/GenreSelect/enums';
import { appendAdjustedRevenueFilter } from '@intelligence/utils/revenueScope';

const METRIC_KEY_MAPPING: IRecord<string> = {
  dau: 'au',
  wau: 'au',
  mau: 'au',
};

export const convertParams = (
  metrics: any[],
  params?: IRecord | null,
  isFetchLineChart?: boolean,
  isSearch?: boolean,
): IMetricDataGamesFetchParams => {
  const checkedDataSource = params?.data_sources || [];

  const checkedMetrics = (() => {
    if (isEmpty(metrics)) return [];
    return metrics
      .filter((metricData) => {
        const dataSource = metricData?.dataSource || '';
        return !!checkedDataSource?.includes(dataSource);
      })
      .map((metricData) => {
        const dataSource = metricData?.dataSource || '';
        const metric = metricData?.metric || '';
        return generateMetricKey({ dataSource, metric });
      });
  })();

  const metricFilter = (() => {
    const list = params?.metric_filter || {};
    return Object.entries(list).reduce((acc: any, cur: any) => {
      const [key, value] = cur || [];
      const valueNew =
        value?.map((item: any) => {
          const metricKey = (() => {
            const result = generateMetricKey({ dataSource: key, metric: item?.metric });
            if (item?.ratio_type !== 1) return result;
            return generateMetricRatioKey(result);
          })();
          return { ...item, metric: metricKey };
        }) || [];

      return [...acc, ...valueNew];
    }, []);
  })();

  /**
   * 校验排序指标的数据源
   * 如果当前候选的数据源集合包含当前排序指标的数据源，直接返回当前排序指标的数据源即可
   * 否则返回当前勾选数据源集合中的第一个数据源
   */
  const orderSource = (() => {
    let orderSourceTmp = params?.order_source || '';
    if (checkedDataSource?.includes(orderSourceTmp)) {
      return orderSourceTmp;
    }
    orderSourceTmp = checkedDataSource?.[0] || '';
    return orderSourceTmp;
  })();

  /**
   * 校验排序指标是否在可选的 metrics 列表中
   * 如果 order_metric 对应的指标 key 不在 checkedMetrics 中，则回退到 checkedMetrics 的第一个指标
   */
  const validatedOrderMetric = (() => {
    const rawOrderMetric = METRIC_KEY_MAPPING[params?.order_metric] || params?.order_metric || '';
    const orderMetricKey = generateMetricKey({ dataSource: orderSource, metric: rawOrderMetric });
    if (checkedMetrics?.length && !checkedMetrics.includes(orderMetricKey)) {
      // 排序指标不在可选 metrics 中，回退到第一个可用指标
      const firstMetricKey = checkedMetrics[0] || '';
      // 从第一个 metricKey 中解析出 dataSource 和 metric（格式为 "dataSource__metric"）
      const parts = firstMetricKey.split('__');
      return {
        orderSource: parts[0] || orderSource,
        orderMetric: parts[1] || rawOrderMetric,
      };
    }
    return { orderSource, orderMetric: rawOrderMetric };
  })();

  const orderBy = (() => {
    const orderMetricData = {
      dataSource: validatedOrderMetric.orderSource,
      metric: validatedOrderMetric.orderMetric,
      ratio: params?.ratio_type === 1,
    };

    return generateSortMetricKey(orderMetricData, params?.order);
  })();

  const backUpOrderBy = (() => {
    const orderMetricKey = generateMetricKey({
      dataSource: validatedOrderMetric.orderSource,
      metric: validatedOrderMetric.orderMetric,
    });
    const idx = checkedMetrics?.indexOf(orderMetricKey);
    let backUpOrderMetricKey = checkedMetrics?.[(idx + 1) % checkedMetrics?.length] || '';
    if (params?.ratio_type === 1) {
      backUpOrderMetricKey = generateMetricRatioKey(backUpOrderMetricKey);
    }
    return `${backUpOrderMetricKey} ${params?.order}`;
  })();

  const appMagicGenres = params?.appmagic_genre || [];
  const appMagicGenresFormatted: IAgentGenreValue[] = formatAppMagicGenres(appMagicGenres);

  const convertedParams = {
    filter: {
      appannie_genre: params?.appannie_genre || [],
      sensortower_genre: params?.sensortower_genre || [],
      iegg_genre: params?.iegg_genre || [],
      // appmagic_genre: appMagicGenres,
      app_magic: {
        relation: Relation.Or,
        tags: appMagicGenresFormatted,
      },
      app_magic_tags: params?.app_magic_tags || {},
      start_release_date: params?.start_release_time || '',
      end_release_date: params?.end_release_time || '',
      metric_filter: metricFilter,
      tags: buildSensorTowerTagsParams(params?.sensortower_tag_filter),
      companies: params?.company || [],
      arpu_min: params?.arpu_min,
      arpu_max: params?.arpu_max,
      au_min: params?.au_min,
      au_max: params?.au_max,
      revenue_min: params?.revenue_min,
      revenue_max: params?.revenue_max,
      downloads_min: params?.downloads_min,
      downloads_max: params?.downloads_max,
      game_engine: params?.game_engine || [],
      game_engine_relation: params?.game_engine_relation || 'or',
    },
    metrics: checkedMetrics,
    game_ids: isSearch ? [] : (params?.game_code?.length > 0 ? params?.game_code : params?.filter_ids) || [],
    rank_game_ids: !isSearch ? [] : params?.game_code || [],
    markets: params?.regions || [],
    platforms: params?.platforms || [],
    granularity: params?.date_type || 'daily',
    start_date: params?.start_date || '',
    end_date: params?.end_date || '',
    group_by: !!isFetchLineChart ? ['date', 'game_id'] : ['game_id'],
    order_by: !!isFetchLineChart ? ['date asc', orderBy] : [orderBy],
    backup_order_by: [backUpOrderBy],
    keyword: '',
    page: params?.page ?? 1,
    page_size: params?.page_size ?? 100,
    tag: params?.tag,
  };

  const surgeMetric = params?.surge_metric?.trim() || '';
  if (isNotEmpty(surgeMetric)) {
    // 获取异动指标对应的排序指标 key（dau/wau/mau 映射为 au）
    const surgeMetricKey = METRIC_KEY_MAPPING[surgeMetric] || surgeMetric;
    const surgeMetricData = { dataSource: orderSource, metric: surgeMetricKey };
    const surgeMetricOrderBy = generateSortMetricKey(surgeMetricData, SortType.Desc);
    Object.assign(convertedParams, {
      rotation_order_by: {
        top_size: 500,
        order: [surgeMetricOrderBy],
      },
    });
  }

  const convertedParamsWithAdjustRevenue = appendAdjustedRevenueFilter(
    convertedParams,
    params?.revenue_scope,
    params?.adjusted_revenue,
  );
  Object.assign(convertedParams, convertedParamsWithAdjustRevenue);

  return convertedParams;
};
